/**
 * Utility functions for financial calculations
 */
import { 
  Config, 
  Params,
  OperatingScenario 
} from '../types/config';
import {
  BreakevenResults,
  CostData, 
  CostItem,
  IRRResults,
  NPVResults
} from '../types/calculations';

/**
 * Calculate Present Value
 * @param futureValue - The future value
 * @param years - Number of years in the future
 * @param discountRate - Discount rate (e.g., 0.05 for 5%)
 * @returns Present value
 */
export const calculatePresentValue = (
  futureValue: number, 
  years: number, 
  discountRate: number
): number => {
  return futureValue / Math.pow(1 + discountRate, years);
};

/**
 * Calculate yearly costs with discounting
 * @param config - System configuration
 * @param params - System parameters
 * @param scenario - Operating scenario
 * @returns Cost data for all energy options
 */
export const calculateYearlyCostsPerKwh = (
  config: Config,
  params: Params,
  scenario: OperatingScenario
): CostData => {
  const currentConfig = config[scenario];
  const years = Array.from({ length: params.batteryAnalysisYears + 1 }, (_, i) => i);
  
  // 100% Grid
  const gridOnlyCostsPerKwh = years.map(year => {
    if (year === 0) return null;
    
    // Annual costs in nominal terms
    const annualCost = currentConfig.dailyConsumption * params.gridRate * 1000 * 365;
    
    // Discount to present value - used in cumulative calculation below
    
    // Cumulative PV costs
    const cumulativePVCost = Array.from({ length: year }, (_, index) => 
      calculatePresentValue(annualCost, index, params.discountRate)
    ).reduce((sum, cost) => sum + cost, 0);
    
    // Total energy over the period
    const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
    
    // Levelized cost
    const levelizedCost = cumulativePVCost / totalKwh;
    
    return { 
      year, 
      value: levelizedCost,
      nominalValue: params.gridRate,
      annualCost: annualCost,
      cumulativeCost: annualCost * year
    };
  }).filter((item): item is CostItem => item !== null);
  
  // Grid-Tied Solar
  const gridTiedCostsPerKwh = years.map(year => {
    if (year === 0) return null;
    
    const initialInvestment = params.solarCost + params.connectionCost;
    
    // Annual operational costs
    const annualGridCost = (currentConfig.gridNeeded * params.gridRate * 1000 * 365);
    const annualFeedInCredit = (currentConfig.excessSolar * params.feedInEffectiveRate * 1000 * 365);
    const annualOperatingCost = annualGridCost - annualFeedInCredit;
    
    // Present value calculation handled in cumulative calculation below
    
    // Cumulative PV costs
    const cumulativePVOperatingCost = Array.from({ length: year }, (_, i) => 
      calculatePresentValue(annualOperatingCost, i, params.discountRate)
    ).reduce((sum, cost) => sum + cost, 0);
    
    const totalPVCost = initialInvestment + cumulativePVOperatingCost;
    
    // Total energy over the period
    const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
    
    // Levelized cost
    const levelizedCost = totalPVCost / totalKwh;
    
    return { 
      year, 
      value: levelizedCost,
      nominalValue: annualOperatingCost / (currentConfig.dailyConsumption * 1000 * 365),
      annualCost: annualOperatingCost,
      cumulativeCost: initialInvestment + (annualOperatingCost * year)
    };
  }).filter((item): item is CostItem => item !== null);
  
  // Solar + Battery
  const batteryCostsPerKwh = years.map(year => {
    if (year === 0) return null;
    
    const totalSystemCost = params.solarCost + params.batteryCost;
    const initialInvestment = totalSystemCost + params.connectionCost;
    
    // Calculate maintenance costs
    const maintenanceYears = params.batteryMaintenanceYears.filter(y => y <= year);
    const maintenanceCosts = maintenanceYears.map((y) => 
      calculatePresentValue(params.maintenanceCost, y - 1, params.discountRate)
    );
    const totalMaintenancePV = maintenanceCosts.reduce((sum, cost) => sum + cost, 0);
    
    // Annual operational costs
    const annualGridCost = (currentConfig.gridNeededWithBattery * params.gridRate * 1000 * 365);
    
    // Present value calculation handled in cumulative calculation below
    
    // Cumulative PV costs
    const cumulativePVOperatingCost = Array.from({ length: year }, (_, i) => 
      calculatePresentValue(annualGridCost, i, params.discountRate)
    ).reduce((sum, cost) => sum + cost, 0);
    
    const totalPVCost = initialInvestment + totalMaintenancePV + cumulativePVOperatingCost;
    
    // Total energy over the period
    const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
    
    // Levelized cost
    const levelizedCost = totalPVCost / totalKwh;
    
    // Calculate maintenance in nominal terms
    const maintenanceCount = params.batteryMaintenanceYears.filter(y => y <= year).length;
    const maintenanceCostNominal = maintenanceCount * params.maintenanceCost;
    
    return { 
      year, 
      value: levelizedCost,
      nominalValue: annualGridCost / (currentConfig.dailyConsumption * 1000 * 365),
      annualCost: annualGridCost,
      cumulativeCost: initialInvestment + maintenanceCostNominal + (annualGridCost * year)
    };
  }).filter((item): item is CostItem => item !== null);
  
  // Generator Only
  const generatorHourlyCost = 2 * params.generatorConsumption * params.generatorFuelCost;
  const generatorCostsPerKwh = years.map(year => {
    if (year === 0) return null;
    
    const generatorHours = currentConfig.operatingHours;
    const annualGeneratorCost = (generatorHourlyCost * generatorHours * 365);
    
    // Present value calculation handled in cumulative calculation below
    
    // Cumulative PV costs
    const cumulativePVCost = Array.from({ length: year }, (_, index) => 
      calculatePresentValue(annualGeneratorCost, index, params.discountRate)
    ).reduce((sum, cost) => sum + cost, 0);
    
    // Total energy over the period
    const totalKwh = year * (currentConfig.dailyConsumption * 1000 * 365);
    
    // Levelized cost
    const levelizedCost = cumulativePVCost / totalKwh;
    
    return { 
      year, 
      value: levelizedCost,
      nominalValue: annualGeneratorCost / (currentConfig.dailyConsumption * 1000 * 365),
      annualCost: annualGeneratorCost,
      cumulativeCost: annualGeneratorCost * year
    };
  }).filter((item): item is CostItem => item !== null);
  
  return {
    gridOnly: gridOnlyCostsPerKwh,
    gridTied: gridTiedCostsPerKwh,
    battery: batteryCostsPerKwh,
    generator: generatorCostsPerKwh
  };
};

/**
 * Calculate NPV for each option
 * @param config - System configuration
 * @param params - System parameters
 * @param scenario - Operating scenario
 * @returns NPV results for all options
 */
export const calculateNPV = (
  config: Config,
  params: Params,
  scenario: OperatingScenario
): NPVResults => {
  const currentConfig = config[scenario];
  const analysisYears = params.batteryAnalysisYears; // Use the battery analysis years as standard
  const extendedAnalysisYears = params.solarAnalysisYears; // Extended for solar components
  const totalSystemCost = params.solarCost + params.batteryCost;
  
  // Calculate grid rate (no escalation)
  const getGridRate = (): number => {
    return params.gridRate;
  };
  
  // Grid only NPV with fixed electricity costs
  const gridOnlyNPV = Array.from({ length: analysisYears }, (_, i) => {
    const gridRate = getGridRate();
    const yearlyGridCost = currentConfig.dailyConsumption * gridRate * 1000 * 365;
    return calculatePresentValue(yearlyGridCost, i, params.discountRate);
  }).reduce((sum, cost) => sum + cost, 0);
  
  // Grid-tied NPV with fixed electricity costs
  const initialInvestmentGridTied = params.solarCost + params.connectionCost;
  const gridTiedNPV = initialInvestmentGridTied + 
    Array.from({ length: analysisYears }, (_, i) => {
      const gridRate = getGridRate();
      // Fixed feed-in rate (no escalation)
      const feedInRate = params.feedInEffectiveRate;
      
      const yearlyGridCost = (currentConfig.gridNeeded * gridRate * 1000 * 365) - 
                            (currentConfig.excessSolar * feedInRate * 1000 * 365);
      return calculatePresentValue(yearlyGridCost, i, params.discountRate);
    }).reduce((sum, cost) => sum + cost, 0);
  
  // Calculate terminal value (20% of initial solar investment at year 30)
  const solarTerminalValue = params.solarCost * 0.2;
  const solarTerminalValuePV = calculatePresentValue(solarTerminalValue, extendedAnalysisYears, params.discountRate);
  
  // Calculate battery residual value
  const batteryResidualValue = params.batteryCost * params.batteryResidualValue;
  const batteryResidualValuePV = calculatePresentValue(batteryResidualValue, analysisYears, params.discountRate);
  
  // Battery NPV with enhanced methodology
  const initialInvestmentBattery = totalSystemCost + params.connectionCost;
  
  // Calculate PV of maintenance as capitalized improvement
  const maintenancePV = params.batteryMaintenanceYears
    .filter(y => y <= analysisYears)
    .map(y => calculatePresentValue(params.maintenanceCost, y - 1, params.discountRate))
    .reduce((sum, cost) => sum + cost, 0);
  
  // Calculate annual costs
  const batteryAnnualFlows = Array.from({ length: analysisYears }, (_, i) => {
    const gridRate = getGridRate();
    // Grid costs
    const gridCost = currentConfig.gridNeededWithBattery * gridRate * 1000 * 365;
    
    // Additional revenue streams
    const gridServices = params.frequencyRegulationRevenue + params.demandResponseRevenue;
    
    // Net annual cash flow
    return calculatePresentValue(gridCost - gridServices, i, params.discountRate);
  }).reduce((sum, value) => sum + value, 0);
  
  // Complete battery NPV calculation (includes terminal value and residual value)
  const batteryNPV = initialInvestmentBattery + maintenancePV + batteryAnnualFlows - 
                      batteryResidualValuePV - solarTerminalValuePV;
  
  // Generator NPV with escalating fuel costs (assume 3% annual increase)
  const fuelEscalationRate = 0.03;
  const generatorNPV = Array.from({ length: analysisYears }, (_, i) => {
    const escalatedFuelCost = params.generatorFuelCost * Math.pow(1 + fuelEscalationRate, i);
    const escalatedHourlyCost = 2 * params.generatorConsumption * escalatedFuelCost;
    const yearlyGeneratorCost = escalatedHourlyCost * currentConfig.operatingHours * 365;
    return calculatePresentValue(yearlyGeneratorCost, i, params.discountRate);
  }).reduce((sum, cost) => sum + cost, 0);
  
  // Calculate LCOE (Levelized Cost of Energy) for all options
  const totalEnergyProduced = currentConfig.dailyConsumption * 1000 * 365 * analysisYears;
  
  // LCOE = Present value of all costs / Total energy delivered
  const gridOnlyLCOE = gridOnlyNPV / totalEnergyProduced;
  const gridTiedLCOE = gridTiedNPV / totalEnergyProduced;
  const batteryLCOE = batteryNPV / totalEnergyProduced;
  const generatorLCOE = generatorNPV / totalEnergyProduced;
  
  return {
    // NPV of costs
    gridOnly: gridOnlyNPV,
    gridTied: gridTiedNPV,
    battery: batteryNPV,
    generator: generatorNPV,
    
    // Savings vs other options
    gridOnlyVsBatterySavings: gridOnlyNPV - batteryNPV,
    gridTiedVsBatterySavings: gridTiedNPV - batteryNPV,
    generatorVsBatterySavings: generatorNPV - batteryNPV,
    
    // LCOE values
    gridOnlyLCOE,
    gridTiedLCOE,
    batteryLCOE,
    generatorLCOE,
    
    // Terminal and residual values
    batteryResidualValuePV,
    solarTerminalValuePV
  };
};

/**
 * Calculate breakeven points (years)
 * @param costs - Cost data
 * @param config - System configuration
 * @param params - System parameters
 * @param scenario - Operating scenario
 * @returns Breakeven results
 */
export const calculateBreakeven = (
  costs: CostData,
  config: Config,
  params: Params,
  scenario: OperatingScenario
): BreakevenResults => {
  const costData = costs;
  const currentConfig = config[scenario];
  const totalSystemCost = params.solarCost + params.batteryCost;
  
  // For Battery vs Grid
  let batteryVsGridBreakeven = null;
  for (let i = 1; i < costData.gridOnly.length; i++) {
    if (costData.battery[i].cumulativeCost <= costData.gridOnly[i].cumulativeCost) {
      batteryVsGridBreakeven = i;
      break;
    }
  }
  
  // For Battery vs Grid-Tied
  let batteryVsGridTiedBreakeven = null;
  for (let i = 1; i < costData.gridTied.length; i++) {
    if (costData.battery[i].cumulativeCost <= costData.gridTied[i].cumulativeCost) {
      batteryVsGridTiedBreakeven = i;
      break;
    }
  }
  
  // If no intersection found, calculate using simplified approach
  if (batteryVsGridBreakeven === null) {
    const batteryAnnualSavings = (currentConfig.dailyConsumption * params.gridRate * 1000 * 365) - 
                               (currentConfig.gridNeededWithBattery * params.gridRate * 1000 * 365);
    batteryVsGridBreakeven = (totalSystemCost + params.connectionCost) / batteryAnnualSavings;
  }
  
  if (batteryVsGridTiedBreakeven === null) {
    const gridTiedAnnualCost = (currentConfig.gridNeeded * params.gridRate * 1000 * 365) - 
                             (currentConfig.excessSolar * params.feedInEffectiveRate * 1000 * 365);
    const batteryAnnualCost = (currentConfig.gridNeededWithBattery * params.gridRate * 1000 * 365);
    const annualSavings = gridTiedAnnualCost - batteryAnnualCost;
    const additionalInvestment = (totalSystemCost + params.connectionCost) - (params.solarCost + params.connectionCost);
    batteryVsGridTiedBreakeven = additionalInvestment / annualSavings;
  }
  
  return {
    batteryVsGrid: batteryVsGridBreakeven,
    batteryVsGridTied: batteryVsGridTiedBreakeven
  };
};

/**
 * Calculate Internal Rate of Return (IRR)
 * @param config - System configuration
 * @param params - System parameters
 * @param scenario - Operating scenario
 * @returns IRR results
 */
export const calculateIRR = (
  config: Config,
  params: Params,
  scenario: OperatingScenario
): IRRResults => {
  const currentConfig = config[scenario];
  const totalSystemCost = params.solarCost + params.batteryCost;
  
  // Cash flows for Battery vs Grid
  const batteryVsGridCashFlows = [-totalSystemCost - params.connectionCost];
  
  for (let year = 1; year <= params.batteryAnalysisYears; year++) {
    const gridCost = currentConfig.dailyConsumption * params.gridRate * 1000 * 365;
    const batteryCost = currentConfig.gridNeededWithBattery * params.gridRate * 1000 * 365;
    
    let yearCashFlow = gridCost - batteryCost;
    
    // Deduct maintenance costs in appropriate years
    if (params.batteryMaintenanceYears.includes(year)) {
      yearCashFlow -= params.maintenanceCost;
    }
    
    batteryVsGridCashFlows.push(yearCashFlow);
  }
  
  // Estimate IRR iteratively
  const estimateIRR = (cashFlows: number[], iterations = 100): number => {
    let r1 = 0.01; // 1%
    let r2 = 0.5; // 50%
    
    const npv = (rate: number): number => {
      return cashFlows.reduce((sum: number, flow: number, i: number) => {
        return sum + flow / Math.pow(1 + rate, i);
      }, 0);
    };
    
    for (let i = 0; i < iterations; i++) {
      const npv1 = npv(r1);
      const npv2 = npv(r2);
      
      if (Math.abs(npv1) < 0.000001) return r1;
      if (Math.abs(npv2) < 0.000001) return r2;
      
      const r3 = r1 - npv1 * (r2 - r1) / (npv2 - npv1);
      
      r1 = r2;
      r2 = r3;
    }
    
    return r2;
  };
  
  const batteryVsGridIRR = estimateIRR(batteryVsGridCashFlows);
  
  return {
    batteryVsGrid: batteryVsGridIRR
  };
};